import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../hooks/useAuth";
import Loading from "../common/Loading";
import BurndownChart from "./BurndownChart";
import VelocityChart from "./VelocityChart";
import Analytics from "./Analytics";

const Reports = () => {
  const { userProfile } = useAuth();
  const [projects, setProjects] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState("all");
  const [selectedSprint, setSelectedSprint] = useState("all");
  const [dateRange, setDateRange] = useState("30days");

  useEffect(() => {
    const fetchData = async () => {
      if (!userProfile) return;

      try {
        // Get user's projects
        const projectsQuery = query(
          collection(db, "projects"),
          where("members", "array-contains", userProfile.uid)
        );
        const projectsSnapshot = await getDocs(projectsQuery);
        const projectsData = projectsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProjects(projectsData);

        // Get sprints
        const projectIds = projectsData.map((p) => p.id);
        if (projectIds.length > 0) {
          const sprintsQuery = query(
            collection(db, "sprints"),
            where("projectId", "in", projectIds)
          );
          const sprintsSnapshot = await getDocs(sprintsQuery);
          const sprintsData = sprintsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setSprints(sprintsData);
        }

        // Get issues
        const issuesQuery = query(
          collection(db, "issues"),
          where("projectId", "in", projectIds)
        );
        const issuesSnapshot = await getDocs(issuesQuery);
        const issuesData = issuesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setIssues(issuesData);
      } catch (error) {
        console.error("Error fetching report data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userProfile]);

  const calculateMetrics = () => {
    const filteredIssues = issues.filter((issue) => {
      if (selectedProject !== "all" && issue.projectId !== selectedProject)
        return false;
      if (selectedSprint !== "all" && issue.sprintId !== selectedSprint)
        return false;
      return true;
    });

    const totalIssues = filteredIssues.length;
    const completedIssues = filteredIssues.filter(
      (issue) => issue.status === "done"
    ).length;
    const inProgressIssues = filteredIssues.filter(
      (issue) => issue.status === "in-progress"
    ).length;
    const openIssues = filteredIssues.filter((issue) =>
      ["todo", "in-review"].includes(issue.status)
    ).length;

    // FIXED: Better story points parsing
    const totalStoryPoints = filteredIssues.reduce((sum, issue) => {
      const points = parseStoryPoints(issue.storyPoints);
      return sum + points;
    }, 0);

    const completedStoryPoints = filteredIssues
      .filter((issue) => issue.status === "done")
      .reduce((sum, issue) => {
        const points = parseStoryPoints(issue.storyPoints);
        return sum + points;
      }, 0);

    const completionRate =
      totalIssues > 0 ? (completedIssues / totalIssues) * 100 : 0;

    return {
      totalIssues,
      completedIssues,
      inProgressIssues,
      openIssues,
      totalStoryPoints,
      completedStoryPoints,
      completionRate,
    };
  };

  // Helper function to parse story points safely
  const parseStoryPoints = (storyPoints) => {
    if (
      storyPoints === null ||
      storyPoints === undefined ||
      storyPoints === ""
    ) {
      return 0;
    }

    // Convert to number, handling both string and number types
    const points = Number(storyPoints);

    // Return 0 if it's NaN, otherwise return the number
    return isNaN(points) ? 0 : points;
  };

  if (loading) {
    return <Loading text="Loading reports..." />;
  }

  const metrics = calculateMetrics();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Reports & Analytics
        </h1>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Project
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
            >
              <option value="all">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Sprint
            </label>
            <select
              value={selectedSprint}
              onChange={(e) => setSelectedSprint(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
            >
              <option value="all">All Sprints</option>
              {sprints.map((sprint) => (
                <option key={sprint.id} value={sprint.id}>
                  {sprint.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="1year">Last Year</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSelectedProject("all");
                setSelectedSprint("all");
                setDateRange("30days");
              }}
              className="w-full px-3 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <MetricCard
          title="Total Issues"
          value={metrics.totalIssues}
          change={null}
          icon="📋"
          color="blue"
        />
        <MetricCard
          title="Completed"
          value={metrics.completedIssues}
          change={metrics.completionRate}
          icon="✅"
          color="green"
        />
        <MetricCard
          title="In Progress"
          value={metrics.inProgressIssues}
          change={null}
          icon="🔄"
          color="yellow"
        />
        <MetricCard
          title="Total Story Points"
          value={metrics.totalStoryPoints}
          change={null}
          icon="📊"
          color="purple"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BurndownChart
          sprints={sprints}
          issues={issues}
          selectedProject={selectedProject}
        />
        <VelocityChart sprints={sprints} selectedProject={selectedProject} />
      </div>

      <Analytics
        issues={issues}
        projects={projects}
        sprints={sprints}
        filters={{ selectedProject, selectedSprint, dateRange }}
      />
    </div>
  );
};

const MetricCard = ({ title, value, change, total, icon, color }) => {
  const colorClasses = {
    blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
    green:
      "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
    yellow:
      "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400",
    purple:
      "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400",
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {title}
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {value}
            {total !== undefined && (
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">
                / {total}
              </span>
            )}
          </p>
          {change !== null && (
            <p className="text-sm text-green-600 dark:text-green-400 mt-1">
              {change.toFixed(1)}% complete
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full ${colorClasses[color]}`}>
          <span className="text-xl">{icon}</span>
        </div>
      </div>
    </div>
  );
};

export default Reports;
